import { ApprovalHistoryResourceType, IApprovalHistoryEntry, useApprovalHistoryQuery } from '@Queries/ApprovalHistory';
import {
	findRaisedRegEdRound,
	isRegEdDecisionCode,
	REGED_CONDITIONALLY_APPROVED_DECISION_CODE,
	REGED_DECLINED_DECISION_CODE,
	REGED_REVISABLE_DECISION_CODE,
	useRegEdAnnotationDownloadMutation,
} from '@Queries/RegEdCompliance';
import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { getDisplayName } from '../../../../models/NameUtils';
import { getDefaultDateStringValue } from '../../../../models/UiUtils';
import { Button } from '../../../components-v2/Button';
import { Collapsible, CollapsiblePanel, CollapsibleTrigger } from '../../../components-v2/Collapsible';
import { Pill } from '../../../components-v2/Pill';
import { ThemeColors } from '../../../components-v2/Theme/theme';
import { ChevronDown } from '../../svgs/icons/ChevronDown';
import { styleSheet } from './styles';

type PillColor = keyof ThemeColors['pill'];

/** Everything the timeline needs to identify a piece, so a host can hand the same object to both. */
export interface IReviewTimelineResource {
	/** Every round the resource still carries; the live one is resolved from them, never by position. */
	approvalRequests?: Api.ICampaignApprovalRequest[];
	resourceId: string;
	resourceType: ApprovalHistoryResourceType;
}

interface IProps extends IReviewTimelineResource {
	/** Renders bare, for a host whose own card already states the status — one card, one status. */
	embedded?: boolean;
	impersonationContext?: Api.IImpersonationContext;
	/** The piece went out without waiting, so its stamped approval records a bypass, not a review. */
	sentWithoutWaiting?: boolean;
	styles?: StyleDeclarationValue[];
}

// The newest unanswered round, else the newest round. A co-approver's open round can precede a later
// one in the array, so index 0 says nothing about which is live.
const resolveLiveRound = (approvalRequests?: Api.ICampaignApprovalRequest[]) =>
	findRaisedRegEdRound(approvalRequests) ?? approvalRequests?.[0];

const hasEmbeddedRounds = (data: IApprovalHistoryEntry[] | null | undefined, isError: boolean) =>
	!!data?.length || (isError && data === undefined);

/** Whether an `embedded` timeline would render anything, for a host that builds chrome around it. */
export const useHasEmbeddedReviewRounds = (
	resource: IReviewTimelineResource | undefined,
	impersonationContext?: Api.IImpersonationContext,
	enabled = true
) => {
	const { data, isError } = useApprovalHistoryQuery({
		resourceId: resource?.resourceId,
		resourceType: resource?.resourceType,
		enabled,
		impersonationContext,
		liveRoundId: resolveLiveRound(resource?.approvalRequests)?.id,
	});
	return hasEmbeddedRounds(data, isError);
};

const getStatusPresentation = (
	response: Api.ICampaignApprovalResponse | undefined
): { label: string; color: PillColor } => {
	if (!response) {
		return { label: 'Revised before review', color: 'gray' };
	}
	switch (response.status) {
		case Api.CampaignApprovalStatus.Approved:
			return response.conditionsPending
				? { label: 'Approved with conditions', color: 'yellow' }
				: { label: 'Approved', color: 'green' };
		case Api.CampaignApprovalStatus.Rejected:
			return { label: 'Rejected', color: 'red' };
		default:
			return { label: 'Decision unknown', color: 'orange' };
	}
};

/**
 * A revision request and a kill both arrive as `Rejected`, so only the firm's code separates them.
 * AdTrax pads it, hence `isRegEdDecisionCode` and the trim.
 */
const getOutcomePresentation = (entry: {
	decisionCode?: string | null;
	response?: Api.ICampaignApprovalResponse;
}): { label: string; color: PillColor } => {
	const { decisionCode, response } = entry;
	if (!decisionCode) {
		return getStatusPresentation(response);
	}
	if (isRegEdDecisionCode(decisionCode, REGED_DECLINED_DECISION_CODE)) {
		return { label: 'Declined', color: 'red' };
	}
	if (isRegEdDecisionCode(decisionCode, REGED_REVISABLE_DECISION_CODE)) {
		return { label: 'Revise and resubmit', color: 'orange' };
	}
	if (isRegEdDecisionCode(decisionCode, REGED_CONDITIONALLY_APPROVED_DECISION_CODE)) {
		return { label: 'Approved with conditions', color: 'yellow' };
	}
	return { label: decisionCode.trim(), color: getStatusPresentation(response).color };
};

const getReviewerName = (approver: Partial<Api.IUser> | Api.IUserReference | undefined) => {
	if (!approver) {
		return 'Unassigned';
	}
	return getDisplayName(approver as Api.IPrincipal) || approver.email || 'Unassigned';
};

const pluralizeRounds = (count: number) => `${count} ${count === 1 ? 'round' : 'rounds'}`;

export const ComplianceReviewTimeline: React.FC<IProps> = ({
	approvalRequests,
	embedded = false,
	impersonationContext,
	resourceId,
	resourceType,
	sentWithoutWaiting = false,
	styles = [],
}) => {
	const [open, setOpen] = React.useState(false);
	const currentRound = resolveLiveRound(approvalRequests);
	const { data, isError } = useApprovalHistoryQuery({
		resourceId,
		resourceType,
		impersonationContext,
		liveRoundId: currentRound?.id,
	});
	const annotationDownload = useRegEdAnnotationDownloadMutation({
		onError: () => toast.error("Couldn't download the reviewer's notes. Please try again."),
	});

	const history = data ?? [];
	// A failed background refetch keeps the rounds it already had.
	const showError = isError && data === undefined;
	const showCurrentRound = !embedded && !!currentRound;
	const liveRoundCount = currentRound ? 1 : 0;

	const handleDownloadAnnotation = (entry: IApprovalHistoryEntry, annotation: { id?: string }) => {
		if (annotationDownload.isPending || !entry.id || !annotation.id) {
			return;
		}
		annotationDownload.mutate({ parentId: resourceId, approvalRequestId: entry.id, annotation, impersonationContext });
	};

	const renderMeta = (label: string, value: string) => (
		<div className={css(styleSheet.metaRow)}>
			<span className={css(styleSheet.metaLabel)}>{label}</span>
			<span className={css(styleSheet.metaValue)}>{value}</span>
		</div>
	);

	const renderEntry = ({
		key,
		roundNumber,
		outcome,
		reviewer,
		isLast,
		isCurrent,
		submittedDate,
		decidedDate,
		revisedDate,
		entry,
	}: {
		key: string;
		roundNumber?: number;
		outcome: { label: string; color: PillColor };
		reviewer?: string;
		isLast: boolean;
		isCurrent: boolean;
		submittedDate?: string;
		decidedDate?: string;
		revisedDate?: string;
		entry?: IApprovalHistoryEntry;
	}) => {
		const notes = (entry?.reviewNotes ?? []).filter(note => !!note.noteText?.trim());
		const annotations = (entry?.annotations ?? []).filter(annotation => !!annotation.id);
		return (
			<li className={css(styleSheet.entry)} key={key}>
				<div className={css(styleSheet.rail)}>
					<div className={css(styleSheet.dot, isCurrent && styleSheet.dotCurrent)} />
					{!isLast && <div className={css(styleSheet.connector)} />}
				</div>
				<div className={css(styleSheet.entryBody, isLast && styleSheet.entryBodyLast)}>
					<div className={css(styleSheet.entryHeader)}>
						<span className={css(styleSheet.roundLabel)}>
							{roundNumber ? `Round ${roundNumber}` : isCurrent ? 'Current round' : 'Latest round'}
						</span>
						<Pill variant='label' color={outcome.color} label={outcome.label} />
					</div>
					{!!reviewer && renderMeta('Reviewer', reviewer)}
					{!!submittedDate && renderMeta('Submitted', getDefaultDateStringValue(submittedDate))}
					{!!decidedDate && renderMeta('Decided', getDefaultDateStringValue(decidedDate))}
					{!!revisedDate && renderMeta('Revised', getDefaultDateStringValue(revisedDate))}
					{notes.map((note, i) => (
						<div className={css(styleSheet.note)} key={`${key}-note-${i}`}>
							<div className={css(styleSheet.noteText)}>{note.noteText}</div>
							{!!note.addedByName && <div className={css(styleSheet.noteByline)}>{note.addedByName}</div>}
						</div>
					))}
					{annotations.length > 0 && (
						<div className={css(styleSheet.annotations)}>
							{annotations.map(annotation => (
								<Button
									key={annotation.id}
									variant='link'
									size='sm'
									styles={[styleSheet.annotationLink]}
									disabled={annotationDownload.isPending}
									onClick={() => handleDownloadAnnotation(entry!, annotation)}
								>
									{annotation.fileName || 'Reviewer annotation'}
								</Button>
							))}
						</div>
					)}
					{!!entry?.providerReferenceId && renderMeta('Tracking #', entry.providerReferenceId)}
				</div>
			</li>
		);
	};

	const renderEarlierRounds = () => (
		<ul className={css(styleSheet.list)}>
			{history.map((entry: IApprovalHistoryEntry, i: number) =>
				renderEntry({
					key: entry.id ?? `round-${i}`,
					roundNumber: history.length - i,
					outcome: getOutcomePresentation(entry),
					reviewer: getReviewerName(entry.approver),
					isLast: i === history.length - 1,
					isCurrent: false,
					submittedDate: entry.submittedToProviderAt ?? undefined,
					decidedDate: entry.response?.creationDate,
					revisedDate: entry.supersededDate,
					entry,
				})
			)}
		</ul>
	);

	// Otherwise an embedded panel on a first-round piece is a rule and a control over nothing.
	if (!showCurrentRound && !hasEmbeddedRounds(data, isError)) {
		return null;
	}

	return (
		<Collapsible
			className={css(embedded ? styleSheet.embedded : styleSheet.card, ...styles)}
			onOpenChange={setOpen}
			open={open}
		>
			{!embedded && (
				<div className={css(styleSheet.header)}>
					<div className={css(styleSheet.title)}>Compliance Review</div>
					{!!data && (
						<div className={css(styleSheet.roundCount)}>{pluralizeRounds(history.length + liveRoundCount)}</div>
					)}
				</div>
			)}
			{showCurrentRound && (
				<ul className={css(styleSheet.list)}>
					{renderEntry({
						key: currentRound.id ?? 'current',
						outcome: sentWithoutWaiting
							? { label: 'Sent without review', color: 'gray' }
							: currentRound.response
								? getStatusPresentation(currentRound.response)
								: { label: 'In review', color: 'yellow' },
						reviewer: sentWithoutWaiting ? undefined : getReviewerName(currentRound.approver),
						isLast: true,
						// Still the live round once it has an answer, but no longer the current one: this
						// renders beside sent and scheduled pieces, whose review is over.
						isCurrent: !currentRound.response,
						decidedDate: sentWithoutWaiting ? undefined : currentRound.response?.creationDate,
					})}
				</ul>
			)}
			{showError ? (
				<>
					<div className={css(styleSheet.rule)} />
					<div className={css(styleSheet.message)}>We couldn&apos;t load the earlier rounds.</div>
				</>
			) : history.length > 0 ? (
				<>
					<div className={css(styleSheet.rule)} />
					<CollapsibleTrigger
						render={
							<Button variant='link' size='sm' styles={[styleSheet.trigger]}>
								<ChevronDown className={css(styleSheet.chevron, open && styleSheet.chevronOpen)} />
								{open ? 'Hide earlier rounds' : 'Show earlier rounds'}
							</Button>
						}
					/>
					<CollapsiblePanel>{renderEarlierRounds()}</CollapsiblePanel>
				</>
			) : null}
		</Collapsible>
	);
};
